import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const GOALS = [
  "Get financially independent", "Land my dream job", "Move into my own place",
  "Get healthier", "Improve relationships", "Start a business",
  "Build better habits", "Understand my rights", "Manage stress & burnout",
  "Invest for the future", "Negotiate my salary", "Boost my confidence",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ name: user?.name === "Explorer" ? "" : user?.name || "", age: "", city: "", goals: [] });
  const [busy, setBusy] = useState(false);

  const steps = [
    { title: "What's your name?", sub: "So we know what to call you.", key: "name" },
    { title: "How old are you?", sub: "We tailor scenarios to your life stage.", key: "age" },
    { title: "Where do you live?", sub: "City helps us localize examples.", key: "city" },
    { title: "What are your goals?", sub: "Pick a few — the coach personalizes around these.", key: "goals" },
  ];

  const cur = steps[step];
  const canNext = () => {
    if (cur.key === "goals") return data.goals.length > 0;
    return String(data[cur.key]).trim().length > 0;
  };

  const toggleGoal = (g) => setData((d) => ({ ...d, goals: d.goals.includes(g) ? d.goals.filter((x) => x !== g) : [...d.goals, g] }));

  const finish = async () => {
    setBusy(true);
    try {
      const payload = { name: data.name, city: data.city, goals: data.goals, age: data.age ? parseInt(data.age) : null };
      const { data: updated } = await api.post("/auth/onboarding", payload);
      setUser(updated);
      toast.success("You're all set! Let's begin.");
      navigate("/app");
    } catch { toast.error("Could not save. Try again."); }
    finally { setBusy(false); }
  };

  const next = () => { if (step < steps.length - 1) setStep(step + 1); else finish(); };

  return (
    <div className="min-h-screen aurora bg-background text-foreground flex flex-col">
      <div className="h-1.5 bg-white/5">
        <motion.div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500" animate={{ width: `${((step + 1) / steps.length) * 100}%` }} transition={{ duration: 0.4 }} />
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-2 mb-8 text-muted-foreground text-xs uppercase tracking-widest font-bold">
            <Sparkles size={14} className="text-indigo-400" /> Step {step + 1} of {steps.length}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
              <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">{cur.title}</h1>
              <p className="text-muted-foreground mb-8">{cur.sub}</p>

              {cur.key === "name" && (
                <Input data-testid="onboard-name" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="Alex" autoFocus className="h-14 text-lg rounded-2xl bg-white/5 border-white/10" />
              )}
              {cur.key === "age" && (
                <Input data-testid="onboard-age" type="number" value={data.age} onChange={(e) => setData({ ...data, age: e.target.value })} placeholder="21" autoFocus className="h-14 text-lg rounded-2xl bg-white/5 border-white/10" />
              )}
              {cur.key === "city" && (
                <Input data-testid="onboard-city" value={data.city} onChange={(e) => setData({ ...data, city: e.target.value })} placeholder="Austin, TX" autoFocus className="h-14 text-lg rounded-2xl bg-white/5 border-white/10" />
              )}
              {cur.key === "goals" && (
                <div className="flex flex-wrap gap-2.5">
                  {GOALS.map((g) => {
                    const on = data.goals.includes(g);
                    return (
                      <button key={g} data-testid={`goal-${g.slice(0, 8)}`} onClick={() => toggleGoal(g)}
                        className={`px-4 py-2.5 rounded-full text-sm font-semibold border transition-all active:scale-95 ${on ? "bg-indigo-600 border-indigo-500 text-white" : "bg-white/5 border-white/10 text-muted-foreground hover:border-white/25"}`}>
                        {on && <Check size={14} className="inline mr-1.5 -mt-0.5" />}{g}
                      </button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center gap-3 mt-10">
            {step > 0 && (
              <Button data-testid="onboard-back" onClick={() => setStep(step - 1)} variant="ghost" className="rounded-2xl h-12 px-5 hover:bg-white/5"><ArrowLeft size={18} className="mr-1" /> Back</Button>
            )}
            <Button data-testid="onboard-next" onClick={next} disabled={!canNext() || busy} className="flex-1 h-12 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-base transition-all hover:-translate-y-0.5 active:scale-95">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <>{step === steps.length - 1 ? "Start My Journey" : "Continue"} <ArrowRight size={18} className="ml-1" /></>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
